/**
 * Shopify Accessibility Widget
 * Modeled on accessiBe, UserWay, All in One Accessibility
 * Vanilla JS — no external dependencies
 * Features: 8 profiles, 25+ individual toggles, TTS, localStorage persistence
 */
(function () {
  'use strict';

  // ── Config from Liquid (or defaults) ──────────────────────────────────────
  const cfg = window.A11yWidgetConfig || {};
  const CONFIG = {
    position:    cfg.position    || 'bottom-right',
    accentColor: cfg.accentColor || '#5B5BD6',
    buttonSize:  cfg.buttonSize  || 56,
    title:       cfg.title       || 'Accessibility Options',
    profiles: Object.assign({
      lowVision: true, motor: true, dyslexia: true, colorBlind: true,
      adhd: true, seizure: true, elderly: true, cognitive: true
    }, cfg.profiles || {}),
    sections: Object.assign({
      content: true, color: true, cursor: true, navigation: true, tools: true
    }, cfg.sections || {})
  };

  // ── State ──────────────────────────────────────────────────────────────────
  const DEFAULT_STATE = {
    fontSize: 0,         // 0–4 (steps: 100%, 120%, 140%, 160%, 200%)
    lineHeight: 0,       // 0=normal, 1=1.8, 2=2.2
    letterSpacing: 0,    // 0=normal, 1=0.05em, 2=0.12em
    wordSpacing: 0,      // 0=normal, 1=0.1em, 2=0.2em
    dyslexiaFont: false,
    boldText: false,
    textAlign: 'default',
    contrast: 'none',    // 'none'|'high'|'dark'|'light'|'invert'
    colorFilter: 'none', // 'none'|'grayscale'|'protanopia'|'deuteranopia'|'tritanopia'|'achromatopsia'
    saturation: 'normal',// 'normal'|'low'|'high'
    cursor: 'default',   // 'default'|'big'|'black'|'white'
    highlightLinks: false,
    highlightHeadings: false,
    pauseAnimations: false,
    hideImages: false,
    readingMask: false,
    readingGuide: false,
    focusIndicators: false,
    muteSounds: false,
    activeProfile: null,
    ttsSpeed: 1
  };

  let state = JSON.parse(JSON.stringify(DEFAULT_STATE));
  let panelOpen = false;
  let ttsUtterance = null;
  let ttsActive = false;
  let maskMoveHandler = null;
  let guideMoveHandler = null;

  // ── Profile presets ────────────────────────────────────────────────────────
  const PROFILES = {
    lowVision:  { fontSize: 3, contrast: 'high', cursor: 'big', focusIndicators: true },
    motor:      { focusIndicators: true, fontSize: 1, cursor: 'big', highlightLinks: true },
    dyslexia:   { dyslexiaFont: true, letterSpacing: 2, lineHeight: 2, wordSpacing: 1, textAlign: 'left' },
    colorBlind: { colorFilter: 'deuteranopia', highlightLinks: true, highlightHeadings: true },
    adhd:       { pauseAnimations: true, readingMask: true, focusIndicators: true, hideImages: false },
    seizure:    { pauseAnimations: true, muteSounds: true },
    elderly:    { fontSize: 2, cursor: 'big', contrast: 'high', boldText: true, focusIndicators: true },
    cognitive:  { pauseAnimations: true, lineHeight: 2, fontSize: 1, highlightLinks: true, readingGuide: true }
  };

  const FONT_SCALES = [1, 1.2, 1.4, 1.6, 2.0];
  const LS_KEY = 'a11y-widget-settings';

  // ── Helpers ────────────────────────────────────────────────────────────────
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const body = document.body;
  const html = document.documentElement;

  function setBodyClass(cls, active) {
    body.classList.toggle(cls, active);
  }

  function clearBodyClasses(prefix) {
    body.className = body.className.split(' ').filter(c => !c.startsWith(prefix)).join(' ');
  }

  function setCSSVar(name, value) {
    document.documentElement.style.setProperty(name, value);
  }

  // ── LocalStorage ───────────────────────────────────────────────────────────
  function saveSettings() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function loadSettings() {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) {
        state = Object.assign({}, DEFAULT_STATE, JSON.parse(saved));
      }
    } catch (e) {}
  }

  // ── Apply all settings to the page ────────────────────────────────────────
  function applyAllSettings() {
    applyFontSize();
    applyLineHeight();
    applyLetterSpacing();
    applyWordSpacing();
    applyDyslexiaFont();
    applyBoldText();
    applyTextAlign();
    applyContrast();
    applyColorFilter();
    applySaturation();
    applyCursor();
    applyHighlightLinks();
    applyHighlightHeadings();
    applyPauseAnimations();
    applyHideImages();
    applyReadingMask();
    applyReadingGuide();
    applyFocusIndicators();
    applyMuteSounds();
    applyAccentColor();
    syncAllControls();
  }

  // ── Feature appliers ───────────────────────────────────────────────────────
  function applyFontSize() {
    const scale = FONT_SCALES[Math.max(0, Math.min(4, state.fontSize))];
    setCSSVar('--a11y-font-scale', scale);
    html.classList.toggle('a11y-font-scaled', scale !== 1);
  }

  function applyLineHeight() {
    body.classList.remove('a11y-line-height-1', 'a11y-line-height-2');
    if (state.lineHeight > 0) body.classList.add(`a11y-line-height-${state.lineHeight}`);
  }

  function applyLetterSpacing() {
    body.classList.remove('a11y-letter-spacing-1', 'a11y-letter-spacing-2');
    if (state.letterSpacing > 0) body.classList.add(`a11y-letter-spacing-${state.letterSpacing}`);
  }

  function applyWordSpacing() {
    body.classList.remove('a11y-word-spacing-1', 'a11y-word-spacing-2');
    if (state.wordSpacing > 0) body.classList.add(`a11y-word-spacing-${state.wordSpacing}`);
  }

  function applyDyslexiaFont() {
    setBodyClass('a11y-dyslexia', state.dyslexiaFont);
  }

  function applyBoldText() {
    setBodyClass('a11y-bold', state.boldText);
  }

  function applyTextAlign() {
    body.classList.remove('a11y-align-left', 'a11y-align-center', 'a11y-align-right', 'a11y-align-justify');
    if (state.textAlign !== 'default') body.classList.add(`a11y-align-${state.textAlign}`);
  }

  function applyContrast() {
    html.classList.remove('a11y-contrast-high', 'a11y-contrast-dark', 'a11y-contrast-light', 'a11y-contrast-invert');
    body.classList.remove('a11y-contrast-high', 'a11y-contrast-dark', 'a11y-contrast-light', 'a11y-contrast-invert');
    if (state.contrast !== 'none') html.classList.add(`a11y-contrast-${state.contrast}`);
  }

  function applyColorFilter() {
    html.classList.remove(
      'a11y-filter-grayscale', 'a11y-filter-protanopia',
      'a11y-filter-deuteranopia', 'a11y-filter-tritanopia', 'a11y-filter-achromatopsia'
    );
    body.classList.remove(
      'a11y-filter-grayscale', 'a11y-filter-protanopia',
      'a11y-filter-deuteranopia', 'a11y-filter-tritanopia', 'a11y-filter-achromatopsia'
    );
    if (state.colorFilter !== 'none') html.classList.add(`a11y-filter-${state.colorFilter}`);
  }

  function applySaturation() {
    body.classList.remove('a11y-saturate-low', 'a11y-saturate-high');
    if (state.saturation === 'low')  body.classList.add('a11y-saturate-low');
    if (state.saturation === 'high') body.classList.add('a11y-saturate-high');
  }

  function applyCursor() {
    body.classList.remove('a11y-cursor-big', 'a11y-cursor-black', 'a11y-cursor-white');
    if (state.cursor !== 'default') body.classList.add(`a11y-cursor-${state.cursor}`);
  }

  function applyHighlightLinks() {
    setBodyClass('a11y-highlight-links', state.highlightLinks);
  }

  function applyHighlightHeadings() {
    setBodyClass('a11y-highlight-headings', state.highlightHeadings);
  }

  function applyPauseAnimations() {
    setBodyClass('a11y-pause-animations', state.pauseAnimations);
    $$('video, audio').forEach(el => {
      if (state.pauseAnimations) { try { el.pause(); } catch(e){} }
    });
  }

  function applyHideImages() {
    setBodyClass('a11y-hide-images', state.hideImages);
  }

  function applyReadingMask() {
    const existingTop = document.getElementById('a11y-reading-mask-top');
    const existingBot = document.getElementById('a11y-reading-mask-bottom');

    if (!state.readingMask) {
      if (existingTop) existingTop.remove();
      if (existingBot) existingBot.remove();
      if (maskMoveHandler) document.removeEventListener('mousemove', maskMoveHandler);
      maskMoveHandler = null;
      return;
    }

    const maskHeight = 60;
    const top = existingTop || document.createElement('div');
    const bot = existingBot || document.createElement('div');
    top.id = 'a11y-reading-mask-top';
    bot.id = 'a11y-reading-mask-bottom';
    document.body.appendChild(top);
    document.body.appendChild(bot);

    maskMoveHandler = function(e) {
      const y = e.clientY;
      top.style.top = '0';
      top.style.height = `${Math.max(0, y - maskHeight / 2)}px`;
      bot.style.top = `${y + maskHeight / 2}px`;
      bot.style.height = `${Math.max(0, window.innerHeight - y - maskHeight / 2)}px`;
    };
    document.addEventListener('mousemove', maskMoveHandler);
  }

  function applyReadingGuide() {
    const existing = document.getElementById('a11y-reading-guide');

    if (!state.readingGuide) {
      if (existing) existing.remove();
      if (guideMoveHandler) document.removeEventListener('mousemove', guideMoveHandler);
      guideMoveHandler = null;
      return;
    }

    const guide = existing || document.createElement('div');
    guide.id = 'a11y-reading-guide';
    document.body.appendChild(guide);

    guideMoveHandler = function(e) {
      guide.style.top = `${e.clientY}px`;
    };
    document.addEventListener('mousemove', guideMoveHandler);
  }

  function applyFocusIndicators() {
    setBodyClass('a11y-focus-indicators', state.focusIndicators);
  }

  function applyMuteSounds() {
    $$('video, audio').forEach(el => {
      el.muted = state.muteSounds;
    });
  }

  function applyAccentColor() {
    setCSSVar('--a11y-accent', CONFIG.accentColor);
    const darker = adjustColor(CONFIG.accentColor, -20);
    setCSSVar('--a11y-accent-dark', darker);
  }

  function adjustColor(hex, amount) {
    let r = parseInt(hex.slice(1,3),16);
    let g = parseInt(hex.slice(3,5),16);
    let b = parseInt(hex.slice(5,7),16);
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  }

  // ── Reset ──────────────────────────────────────────────────────────────────
  function resetAll() {
    stopTTS();
    state = JSON.parse(JSON.stringify(DEFAULT_STATE));
    saveSettings();

    // Remove all a11y body classes
    const toRemove = Array.from(body.classList).filter(c => c.startsWith('a11y-'));
    toRemove.forEach(c => body.classList.remove(c));
    const htmlToRemove = Array.from(html.classList).filter(c => c.startsWith('a11y-'));
    htmlToRemove.forEach(c => html.classList.remove(c));

    // Remove mask/guide
    ['a11y-reading-mask-top','a11y-reading-mask-bottom','a11y-reading-guide'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.remove();
    });
    if (maskMoveHandler)  document.removeEventListener('mousemove', maskMoveHandler);
    if (guideMoveHandler) document.removeEventListener('mousemove', guideMoveHandler);
    maskMoveHandler = null;
    guideMoveHandler = null;

    // Reset muted media
    $$('video, audio').forEach(el => { el.muted = false; });

    // Reset CSS vars
    setCSSVar('--a11y-font-scale', 1);
    html.classList.remove('a11y-font-scaled');

    applyAccentColor();
    syncAllControls();
  }

  // ── Profile activation ─────────────────────────────────────────────────────
  function activateProfile(profileKey) {
    if (state.activeProfile === profileKey) {
      state.activeProfile = null;
      state = Object.assign({}, DEFAULT_STATE, { activeProfile: null });
    } else {
      state = Object.assign({}, DEFAULT_STATE, PROFILES[profileKey], { activeProfile: profileKey });
    }
    saveSettings();
    applyAllSettings();
  }

  // ── TTS ────────────────────────────────────────────────────────────────────
  function startTTS() {
    if (!window.speechSynthesis) return;
    stopTTS();

    const text = getPageText();
    ttsUtterance = new SpeechSynthesisUtterance(text);
    ttsUtterance.rate = parseFloat(state.ttsSpeed) || 1;
    ttsUtterance.onend = () => {
      ttsActive = false;
      syncTTSButton();
    };
    window.speechSynthesis.speak(ttsUtterance);
    ttsActive = true;
    syncTTSButton();
  }

  function stopTTS() {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    ttsUtterance = null;
    ttsActive = false;
    syncTTSButton();
  }

  function getPageText() {
    const mainEl = $('main') || $('[role="main"]') || $('article') || body;
    return (mainEl.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 5000);
  }

  function syncTTSButton() {
    const btn = document.getElementById('a11y-tts-read-btn');
    if (!btn) return;
    if (ttsActive) {
      btn.textContent = '';
      btn.appendChild(createIcon(ICONS.stop));
      btn.appendChild(document.createTextNode(' Stop Reading'));
    } else {
      btn.textContent = '';
      btn.appendChild(createIcon(ICONS.speaker));
      btn.appendChild(document.createTextNode(' Read Page Aloud'));
    }
  }

  // ── SVG Color-blind filters ────────────────────────────────────────────────
  function injectSVGFilters() {
    if (document.getElementById('a11y-svg-filters')) return;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('id', 'a11y-svg-filters');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none';

    const filters = {
      protanopia: [
        '0.567, 0.433, 0, 0, 0',
        '0.558, 0.442, 0, 0, 0',
        '0, 0.242, 0.758, 0, 0',
        '0, 0, 0, 1, 0'
      ],
      deuteranopia: [
        '0.625, 0.375, 0, 0, 0',
        '0.7, 0.3, 0, 0, 0',
        '0, 0.3, 0.7, 0, 0',
        '0, 0, 0, 1, 0'
      ],
      tritanopia: [
        '0.95, 0.05, 0, 0, 0',
        '0, 0.433, 0.567, 0, 0',
        '0, 0.475, 0.525, 0, 0',
        '0, 0, 0, 1, 0'
      ]
    };

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

    Object.entries(filters).forEach(([name, rows]) => {
      const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
      filter.setAttribute('id', `a11y-filter-${name}`);
      const matrix = document.createElementNS('http://www.w3.org/2000/svg', 'feColorMatrix');
      matrix.setAttribute('type', 'matrix');
      matrix.setAttribute('values', rows.join(' '));
      filter.appendChild(matrix);
      defs.appendChild(filter);
    });

    svg.appendChild(defs);
    document.body.insertBefore(svg, document.body.firstChild);
  }

  // ── Icons (inline SVG strings) ─────────────────────────────────────────────
  const ICONS = {
    accessibility: '<path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm-1 5h2l2 4 2-1.5v-2.5h2V9l-3 2-1 3 2 4h-2l-1.5-3.5L12 18l-1.5 2.5H8.5l2-4-1-3-3-2V7.5h2V10l2 1.5L11 7z"/>',
    eye: '<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>',
    hand: '<path d="M19 11h-2V5a1 1 0 0 0-2 0v6h-2V3a1 1 0 0 0-2 0v8H9V7a1 1 0 0 0-2 0v8c0 3.31 2.69 6 6 6s6-2.69 6-6v-4z"/>',
    text: '<path d="M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z"/>',
    colorBlind: '<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-.34 0-.67.04-1 .1L14.9 16a5.022 5.022 0 0 0-2.9-7z"/>',
    adhd: '<path d="M11 17h2v-6h-2v6zm1-15C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zM11 9h2V7h-2v2z"/>',
    flash: '<path d="M7 2v11h3v9l7-12h-4l4-8z"/>',
    elderly: '<path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z"/>',
    brain: '<path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>',
    close: '<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>',
    reset: '<path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>',
    speaker: '<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>',
    stop: '<path d="M6 6h12v12H6z"/>',
    mask: '<path d="M20 6H4v9c0 2.2 1.8 4 4 4h8c2.2 0 4-1.8 4-4V6zm-8 2c.55 0 1 .45 1 1v3h-2V9c0-.55.45-1 1-1zm-5 1c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm10 0c0 .55-.45 1-1 1s-1-.45-1-1 .45-1 1-1 1 .45 1 1zm-8.5 6c-.83 0-1.5-.67-1.5-1.5S7.67 12 8.5 12s1.5.67 1.5 1.5S9.33 15 8.5 15zm7 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>',
    guide: '<path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>'
  };

  function createIcon(pathData, size) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', size || 20);
    svg.setAttribute('height', size || 20);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.style.cssText = 'display:inline-block;vertical-align:middle;fill:currentColor;flex-shrink:0';
    svg.innerHTML = pathData;
    return svg;
  }

  // ── Build floating button ──────────────────────────────────────────────────
  function buildButton() {
    const btn = document.createElement('button');
    btn.id = 'a11y-toggle-btn';
    btn.setAttribute('aria-label', 'Open ' + CONFIG.title);
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'a11y-panel');
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.style.width = btn.style.height = CONFIG.buttonSize + 'px';

    const pos = CONFIG.position;
    btn.classList.add('a11y-pos-' + pos);
    btn.appendChild(createIcon(ICONS.accessibility, 30));

    btn.addEventListener('click', togglePanel);
    document.body.appendChild(btn);
    return btn;
  }

  // ── Build skip link ────────────────────────────────────────────────────────
  function buildSkipLink() {
    const link = document.createElement('a');
    link.className = 'a11y-skip-link';
    link.href = '#main-content';
    link.textContent = 'Skip to main content';

    // Ensure the target has an id if it's a <main> element
    const main = $('main') || $('[role="main"]');
    if (main && !main.id) main.id = 'main-content';

    document.body.insertBefore(link, document.body.firstChild);
  }

  // ── Build panel ────────────────────────────────────────────────────────────
  function buildPanel() {
    const panel = document.createElement('div');
    panel.id = 'a11y-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'a11y-panel-title');
    panel.setAttribute('tabindex', '-1');
    panel.hidden = true;

    const isLeft = CONFIG.position.includes('left');
    if (isLeft) {
      panel.classList.add('a11y-pos-left');
      panel.style.left = '0';
    } else {
      panel.classList.add('a11y-pos-right');
      panel.style.right = '0';
    }

    // Header
    const header = document.createElement('div');
    header.className = 'a11y-panel__header';

    const title = document.createElement('h2');
    title.id = 'a11y-panel-title';
    title.className = 'a11y-panel__title';
    title.textContent = CONFIG.title;

    const closeBtn = document.createElement('button');
    closeBtn.className = 'a11y-panel__close';
    closeBtn.setAttribute('aria-label', 'Close ' + CONFIG.title);
    closeBtn.appendChild(createIcon(ICONS.close, 18));
    closeBtn.addEventListener('click', closePanel);

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Reset bar
    const resetBar = document.createElement('div');
    resetBar.className = 'a11y-reset-bar';
    const resetBtn = document.createElement('button');
    resetBtn.className = 'a11y-reset-btn';
    resetBtn.setAttribute('aria-label', 'Reset all accessibility settings');
    resetBtn.appendChild(createIcon(ICONS.reset, 13));
    resetBtn.appendChild(document.createTextNode(' Reset All'));
    resetBtn.addEventListener('click', () => { resetAll(); });
    resetBar.appendChild(resetBtn);

    // Body
    const body = document.createElement('div');
    body.className = 'a11y-panel__body';

    panel.appendChild(header);
    panel.appendChild(resetBar);
    panel.appendChild(body);

    // Build sections
    buildProfilesSection(body);
    if (CONFIG.sections.content)    buildContentSection(body);
    if (CONFIG.sections.color)      buildColorSection(body);
    if (CONFIG.sections.cursor)     buildCursorSection(body);
    if (CONFIG.sections.navigation) buildNavigationSection(body);
    if (CONFIG.sections.tools)      buildToolsSection(body);

    document.body.appendChild(panel);
    return panel;
  }

  // ── Section: Profiles ─────────────────────────────────────────────────────
  function buildProfilesSection(container) {
    const profileDefs = [
      { key: 'lowVision', label: 'Low Vision',     icon: ICONS.eye,       enabled: CONFIG.profiles.lowVision },
      { key: 'motor',     label: 'Motor',           icon: ICONS.hand,      enabled: CONFIG.profiles.motor },
      { key: 'dyslexia',  label: 'Dyslexia',        icon: ICONS.text,      enabled: CONFIG.profiles.dyslexia },
      { key: 'colorBlind',label: 'Color Blind',     icon: ICONS.colorBlind,enabled: CONFIG.profiles.colorBlind },
      { key: 'adhd',      label: 'ADHD',            icon: ICONS.adhd,      enabled: CONFIG.profiles.adhd },
      { key: 'seizure',   label: 'Seizure Safe',    icon: ICONS.flash,     enabled: CONFIG.profiles.seizure },
      { key: 'elderly',   label: 'Elderly',         icon: ICONS.elderly,   enabled: CONFIG.profiles.elderly },
      { key: 'cognitive', label: 'Cognitive',       icon: ICONS.brain,     enabled: CONFIG.profiles.cognitive }
    ].filter(p => p.enabled);

    if (!profileDefs.length) return;

    const section = createSection('Accessibility Profiles', 'a11y-profiles-heading');
    const grid = document.createElement('div');
    grid.className = 'a11y-profiles-grid';

    profileDefs.forEach(({ key, label, icon }) => {
      const btn = document.createElement('button');
      btn.className = 'a11y-profile-btn';
      btn.setAttribute('aria-pressed', 'false');
      btn.dataset.profile = key;
      btn.appendChild(createIcon(icon, 24));
      btn.appendChild(document.createTextNode(label));
      btn.addEventListener('click', () => activateProfile(key));
      grid.appendChild(btn);
    });

    section.querySelector('.a11y-section__body').appendChild(grid);
    container.appendChild(section);
  }

  // ── Section: Content Adjustments ─────────────────────────────────────────
  function buildContentSection(container) {
    const section = createSection('Content Adjustments', 'a11y-content-heading');
    const body = section.querySelector('.a11y-section__body');

    // Font size stepper
    body.appendChild(createStepperRow('Text Size', 'fontSize', 0, 4, level => {
      const labels = ['Normal (100%)','Large (120%)','Larger (140%)','Largest (160%)','Huge (200%)'];
      return labels[level];
    }, applyFontSize));

    // Line height stepper
    body.appendChild(createStepperRow('Line Height', 'lineHeight', 0, 2, level => {
      return ['Normal','Relaxed','Spacious'][level];
    }, applyLineHeight));

    // Letter spacing stepper
    body.appendChild(createStepperRow('Letter Spacing', 'letterSpacing', 0, 2, level => {
      return ['Normal','Wide','Wider'][level];
    }, applyLetterSpacing));

    // Word spacing stepper
    body.appendChild(createStepperRow('Word Spacing', 'wordSpacing', 0, 2, level => {
      return ['Normal','Wide','Wider'][level];
    }, applyWordSpacing));

    // Dyslexia font toggle
    body.appendChild(createToggleRow('Dyslexia Font', 'dyslexiaFont', applyDyslexiaFont));

    // Bold text toggle
    body.appendChild(createToggleRow('Bold Text', 'boldText', applyBoldText));

    // Text alignment chips
    const alignRow = document.createElement('div');
    alignRow.style.cssText = 'padding:7px 0;border-top:1px solid #f0f0f0';
    const alignLabel = document.createElement('div');
    alignLabel.className = 'a11y-stepper-label';
    alignLabel.style.marginBottom = '6px';
    alignLabel.textContent = 'Text Alignment';
    const alignChips = document.createElement('div');
    alignChips.className = 'a11y-chips';

    [['default','Default'],['left','Left'],['center','Center'],['right','Right'],['justify','Justify']].forEach(([val, label]) => {
      const chip = createChip(label, 'textAlign', val, () => applyTextAlign());
      alignChips.appendChild(chip);
    });

    alignRow.appendChild(alignLabel);
    alignRow.appendChild(alignChips);
    body.appendChild(alignRow);

    container.appendChild(section);
  }

  // ── Section: Color & Contrast ─────────────────────────────────────────────
  function buildColorSection(container) {
    const section = createSection('Color & Contrast', 'a11y-color-heading');
    const body = section.querySelector('.a11y-section__body');

    // Contrast chips
    const contrastLabel = document.createElement('div');
    contrastLabel.className = 'a11y-stepper-label';
    contrastLabel.style.marginBottom = '6px';
    contrastLabel.textContent = 'Contrast Mode';
    const contrastChips = document.createElement('div');
    contrastChips.className = 'a11y-chips';
    contrastChips.style.marginBottom = '10px';

    [['none','Default'],['high','High Contrast'],['dark','Dark Mode'],['light','Light Mode'],['invert','Invert']].forEach(([val, label]) => {
      contrastChips.appendChild(createChip(label, 'contrast', val, applyContrast));
    });

    body.appendChild(contrastLabel);
    body.appendChild(contrastChips);

    // Color blind filter chips
    const filterLabel = document.createElement('div');
    filterLabel.className = 'a11y-stepper-label';
    filterLabel.style.cssText = 'margin-bottom:6px;border-top:1px solid #f0f0f0;padding-top:10px';
    filterLabel.textContent = 'Color Blind Filter';
    const filterChips = document.createElement('div');
    filterChips.className = 'a11y-chips';
    filterChips.style.marginBottom = '10px';

    [['none','None'],['protanopia','Protanopia'],['deuteranopia','Deuteranopia'],['tritanopia','Tritanopia'],['grayscale','Grayscale'],['achromatopsia','Achromatopsia']].forEach(([val, label]) => {
      filterChips.appendChild(createChip(label, 'colorFilter', val, applyColorFilter));
    });

    body.appendChild(filterLabel);
    body.appendChild(filterChips);

    // Saturation chips
    const satLabel = document.createElement('div');
    satLabel.className = 'a11y-stepper-label';
    satLabel.style.cssText = 'margin-bottom:6px;border-top:1px solid #f0f0f0;padding-top:10px';
    satLabel.textContent = 'Saturation';
    const satChips = document.createElement('div');
    satChips.className = 'a11y-chips';

    [['normal','Normal'],['low','Low'],['high','High']].forEach(([val, label]) => {
      satChips.appendChild(createChip(label, 'saturation', val, applySaturation));
    });

    body.appendChild(satLabel);
    body.appendChild(satChips);

    container.appendChild(section);
  }

  // ── Section: Cursor ────────────────────────────────────────────────────────
  function buildCursorSection(container) {
    const section = createSection('Cursor', 'a11y-cursor-heading');
    const body = section.querySelector('.a11y-section__body');

    const label = document.createElement('div');
    label.className = 'a11y-stepper-label';
    label.style.marginBottom = '8px';
    label.textContent = 'Cursor Style';
    const chips = document.createElement('div');
    chips.className = 'a11y-chips';

    [['default','Default'],['big','Big Cursor'],['black','Black Cursor'],['white','White Cursor']].forEach(([val, lbl]) => {
      chips.appendChild(createChip(lbl, 'cursor', val, applyCursor));
    });

    body.appendChild(label);
    body.appendChild(chips);
    container.appendChild(section);
  }

  // ── Section: Navigation Aids ───────────────────────────────────────────────
  function buildNavigationSection(container) {
    const section = createSection('Navigation Aids', 'a11y-nav-heading');
    const body = section.querySelector('.a11y-section__body');

    body.appendChild(createToggleRow('Highlight Links',    'highlightLinks',    applyHighlightLinks));
    body.appendChild(createToggleRow('Highlight Headings', 'highlightHeadings', applyHighlightHeadings));
    body.appendChild(createToggleRow('Pause Animations',   'pauseAnimations',   applyPauseAnimations));
    body.appendChild(createToggleRow('Hide Images',        'hideImages',        applyHideImages));
    body.appendChild(createToggleRow('Reading Mask',       'readingMask',       applyReadingMask));
    body.appendChild(createToggleRow('Reading Guide',      'readingGuide',      applyReadingGuide));
    body.appendChild(createToggleRow('Focus Indicators',   'focusIndicators',   applyFocusIndicators));
    body.appendChild(createToggleRow('Mute Sounds',        'muteSounds',        applyMuteSounds));

    container.appendChild(section);
  }

  // ── Section: Tools ────────────────────────────────────────────────────────
  function buildToolsSection(container) {
    const section = createSection('Tools', 'a11y-tools-heading');
    const body = section.querySelector('.a11y-section__body');

    const ttsControls = document.createElement('div');
    ttsControls.className = 'a11y-tts-controls';

    if (window.speechSynthesis) {
      const ttsBtn = document.createElement('button');
      ttsBtn.id = 'a11y-tts-read-btn';
      ttsBtn.className = 'a11y-tts-btn';
      ttsBtn.appendChild(createIcon(ICONS.speaker, 16));
      ttsBtn.appendChild(document.createTextNode(' Read Page Aloud'));
      ttsBtn.addEventListener('click', () => {
        ttsActive ? stopTTS() : startTTS();
      });

      const speedRow = document.createElement('div');
      speedRow.className = 'a11y-tts-speed';
      const speedLabel = document.createElement('label');
      speedLabel.setAttribute('for', 'a11y-tts-speed-select');
      speedLabel.textContent = 'Reading speed:';
      const speedSelect = document.createElement('select');
      speedSelect.id = 'a11y-tts-speed-select';
      [['0.75','Slow'],['1','Normal'],['1.25','Fast'],['1.5','Very Fast']].forEach(([val, lbl]) => {
        const opt = document.createElement('option');
        opt.value = val;
        opt.textContent = lbl;
        if (parseFloat(val) === parseFloat(state.ttsSpeed)) opt.selected = true;
        speedSelect.appendChild(opt);
      });
      speedSelect.addEventListener('change', e => {
        state.ttsSpeed = parseFloat(e.target.value);
        saveSettings();
      });

      speedRow.appendChild(speedLabel);
      speedRow.appendChild(speedSelect);
      ttsControls.appendChild(ttsBtn);
      ttsControls.appendChild(speedRow);
    } else {
      const noTTS = document.createElement('p');
      noTTS.style.cssText = 'font-size:12px;color:#888;margin:0';
      noTTS.textContent = 'Text-to-speech is not supported in this browser.';
      ttsControls.appendChild(noTTS);
    }

    body.appendChild(ttsControls);
    container.appendChild(section);
  }

  // ── Section builder helper ─────────────────────────────────────────────────
  function createSection(title, headingId) {
    const section = document.createElement('div');
    section.className = 'a11y-section';

    const heading = document.createElement('h3');
    heading.className = 'a11y-section__heading';
    heading.id = headingId;
    heading.textContent = title;

    const sectionBody = document.createElement('div');
    sectionBody.className = 'a11y-section__body';

    section.appendChild(heading);
    section.appendChild(sectionBody);
    return section;
  }

  // ── Toggle row helper ──────────────────────────────────────────────────────
  function createToggleRow(label, stateKey, applyFn) {
    const row = document.createElement('div');
    row.className = 'a11y-toggle-row';
    row.dataset.stateKey = stateKey;

    const labelEl = document.createElement('label');
    labelEl.className = 'a11y-toggle-label';
    const inputId = 'a11y-toggle-' + stateKey;
    labelEl.setAttribute('for', inputId);
    labelEl.textContent = label;

    const switchEl = document.createElement('div');
    switchEl.className = 'a11y-toggle-switch';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = inputId;
    input.checked = !!state[stateKey];
    input.addEventListener('change', e => {
      state[stateKey] = e.target.checked;
      saveSettings();
      applyFn();
    });

    const track = document.createElement('div');
    track.className = 'a11y-toggle-switch__track';
    track.addEventListener('click', () => { input.click(); });

    switchEl.appendChild(input);
    switchEl.appendChild(track);
    row.appendChild(labelEl);
    row.appendChild(switchEl);
    return row;
  }

  // ── Stepper row helper ─────────────────────────────────────────────────────
  function createStepperRow(label, stateKey, min, max, valLabel, applyFn) {
    const row = document.createElement('div');
    row.className = 'a11y-stepper-row';
    row.dataset.stateKey = stateKey;

    const labelEl = document.createElement('div');
    labelEl.className = 'a11y-stepper-label';
    labelEl.textContent = label;

    const stepper = document.createElement('div');
    stepper.className = 'a11y-stepper';

    const decBtn = document.createElement('button');
    decBtn.className = 'a11y-stepper__btn';
    decBtn.setAttribute('aria-label', `Decrease ${label}`);
    decBtn.textContent = '−';

    const valEl = document.createElement('span');
    valEl.className = 'a11y-stepper__val';
    valEl.setAttribute('aria-live', 'polite');
    valEl.setAttribute('aria-atomic', 'true');
    valEl.textContent = valLabel(state[stateKey]);

    const incBtn = document.createElement('button');
    incBtn.className = 'a11y-stepper__btn';
    incBtn.setAttribute('aria-label', `Increase ${label}`);
    incBtn.textContent = '+';

    function update() {
      valEl.textContent = valLabel(state[stateKey]);
      decBtn.disabled = state[stateKey] <= min;
      incBtn.disabled = state[stateKey] >= max;
    }

    decBtn.addEventListener('click', () => {
      if (state[stateKey] > min) { state[stateKey]--; saveSettings(); applyFn(); update(); }
    });
    incBtn.addEventListener('click', () => {
      if (state[stateKey] < max) { state[stateKey]++; saveSettings(); applyFn(); update(); }
    });

    update();

    stepper.appendChild(decBtn);
    stepper.appendChild(valEl);
    stepper.appendChild(incBtn);

    row.appendChild(labelEl);
    row.appendChild(stepper);
    return row;
  }

  // ── Chip helper ────────────────────────────────────────────────────────────
  function createChip(label, stateKey, value, applyFn) {
    const chip = document.createElement('button');
    chip.className = 'a11y-chip';
    chip.textContent = label;
    chip.setAttribute('aria-pressed', String(state[stateKey] === value));
    chip.dataset.stateKey = stateKey;
    chip.dataset.value = value;

    chip.addEventListener('click', () => {
      state[stateKey] = value;
      saveSettings();
      applyFn();
      // Update siblings
      const parent = chip.closest('.a11y-chips');
      if (parent) {
        parent.querySelectorAll('.a11y-chip').forEach(c => {
          c.setAttribute('aria-pressed', String(c.dataset.value === value && c.dataset.stateKey === stateKey));
        });
      }
    });

    return chip;
  }

  // ── Sync all controls to current state ────────────────────────────────────
  function syncAllControls() {
    if (!document.getElementById('a11y-panel')) return;

    // Stepper rows
    $$('[data-state-key]', document.getElementById('a11y-panel')).forEach(el => {
      const key = el.dataset.stateKey;
      if (el.classList.contains('a11y-toggle-row')) {
        const input = el.querySelector('input[type="checkbox"]');
        if (input) input.checked = !!state[key];
      } else if (el.classList.contains('a11y-stepper-row')) {
        const val = el.querySelector('.a11y-stepper__val');
        const dec = el.querySelector('.a11y-stepper__btn:first-of-type');
        const inc = el.querySelector('.a11y-stepper__btn:last-of-type');
        if (val) {
          const steppers = { fontSize: 4, lineHeight: 2, letterSpacing: 2, wordSpacing: 2 };
          const labels = {
            fontSize: ['Normal (100%)','Large (120%)','Larger (140%)','Largest (160%)','Huge (200%)'],
            lineHeight: ['Normal','Relaxed','Spacious'],
            letterSpacing: ['Normal','Wide','Wider'],
            wordSpacing: ['Normal','Wide','Wider']
          };
          val.textContent = (labels[key] || [])[state[key]] || state[key];
          if (dec) dec.disabled = state[key] <= 0;
          if (inc) inc.disabled = state[key] >= (steppers[key] || 2);
        }
      }
    });

    // Chips
    $$('.a11y-chip').forEach(chip => {
      const key = chip.dataset.stateKey;
      chip.setAttribute('aria-pressed', String(state[key] === chip.dataset.value));
    });

    // Profile buttons
    $$('.a11y-profile-btn').forEach(btn => {
      btn.setAttribute('aria-pressed', String(state.activeProfile === btn.dataset.profile));
    });

    // TTS speed
    const speedSel = document.getElementById('a11y-tts-speed-select');
    if (speedSel) speedSel.value = String(state.ttsSpeed);
  }

  // ── Panel open/close + focus trap ─────────────────────────────────────────
  function openPanel() {
    const panel = document.getElementById('a11y-panel');
    const btn   = document.getElementById('a11y-toggle-btn');
    if (!panel) return;
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('a11y-panel-open'));
    panelOpen = true;
    btn.setAttribute('aria-expanded', 'true');
    panel.focus();
    document.addEventListener('keydown', handlePanelKeydown);
    document.addEventListener('mousedown', handleOutsideClick);
  }

  function closePanel() {
    const panel = document.getElementById('a11y-panel');
    const btn   = document.getElementById('a11y-toggle-btn');
    if (!panel) return;
    panel.classList.remove('a11y-panel-open');
    setTimeout(() => { panel.hidden = true; }, 310);
    panelOpen = false;
    btn.setAttribute('aria-expanded', 'false');
    btn.focus();
    document.removeEventListener('keydown', handlePanelKeydown);
    document.removeEventListener('mousedown', handleOutsideClick);
  }

  function togglePanel() {
    panelOpen ? closePanel() : openPanel();
  }

  function getFocusableEls(panel) {
    return Array.from(panel.querySelectorAll(
      'button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex="0"]'
    )).filter(el => el.offsetParent !== null);
  }

  function handlePanelKeydown(e) {
    const panel = document.getElementById('a11y-panel');
    if (!panel || panel.hidden) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = getFocusableEls(panel);
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function handleOutsideClick(e) {
    const panel = document.getElementById('a11y-panel');
    const btn   = document.getElementById('a11y-toggle-btn');
    if (!panel || panel.hidden) return;
    if (!panel.contains(e.target) && e.target !== btn && !btn.contains(e.target)) {
      closePanel();
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    // Avoid double-init
    if (document.getElementById('a11y-toggle-btn')) return;

    injectSVGFilters();
    buildSkipLink();
    buildButton();
    buildPanel();
    loadSettings();
    applyAllSettings();
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
